/* eslint-disable react/require-default-props */
import * as React from 'react';
import { ErrorMessage, Form, Formik } from 'formik';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Customer, LoadedUser } from '../../interfaces';
import './form.css';
import {
  CreateCustomerValues,
  initFormValues,
  validationSchema,
} from '../../utils/form/customerForm';
import StyledPhoneInput from './PhoneInput';
import {
  addCustomerAlgolia,
  addCustomerDatabase,
  updateCustomer,
} from '../../services/user';
import TextareaInput from './TextareaInput';
import {
  invalidateCustomerOverview,
  isCustomerEditDisabled,
} from '../../services/customers';
import TextInput from './TextInput';
import useUser from '../../hooks/global/useUser';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import { openCustomerChat } from '../../helpers/customers';

interface Props {
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setShouldScrollToTop?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedCustomer?: Customer;
  onCustomerSave?: () => void | Promise<void>;
  openChatOnSave?: boolean;
  submitText?: string;
}

export default function CustomerForm({
  setModalOpen,
  setShouldScrollToTop,
  selectedCustomer,
  onCustomerSave,
  openChatOnSave,
  submitText,
}: Props) {
  const navigate = useNavigate();
  const { user: loadedUser } = useUser<LoadedUser>();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const [formError, setFormError] = React.useState<any>('');
  const [loadedInitValues, setLoadedInitValues] =
    React.useState<CreateCustomerValues>({
      ...initFormValues,
    });

  const disableCustomerEdit =
    !!selectedCustomer && isCustomerEditDisabled(selectedCustomer.user);

  React.useEffect(() => {
    async function loadFormData() {
      if (selectedCustomer) {
        const tempNewInitValues: CreateCustomerValues = { ...initFormValues };

        if (selectedCustomer.user.firstName) {
          tempNewInitValues.firstName = selectedCustomer.user.firstName;
        }

        if (selectedCustomer.user.lastName) {
          tempNewInitValues.lastName = selectedCustomer.user.lastName;
        }

        if (selectedCustomer.user.email) {
          tempNewInitValues.email = selectedCustomer.user.email;
        }

        if (selectedCustomer.user.phoneNumber) {
          tempNewInitValues.phoneNumber = selectedCustomer.user.phoneNumber;
        }

        if (selectedCustomer.dob) {
          const dob = new Date(selectedCustomer.dob)
            .toISOString()
            .split('T')[0];
          tempNewInitValues.dob = dob;
        }

        if (selectedCustomer.internalNotes) {
          tempNewInitValues.internalNotes = selectedCustomer.internalNotes;
        }

        if (selectedCustomer.streetAddress) {
          tempNewInitValues.streetAddress = selectedCustomer.streetAddress;
        }

        if (selectedCustomer.streetAddress2) {
          tempNewInitValues.streetAddress2 = selectedCustomer.streetAddress2;
        }

        if (selectedCustomer.city) {
          tempNewInitValues.city = selectedCustomer.city;
        }

        if (selectedCustomer.state) {
          tempNewInitValues.state = selectedCustomer.state;
        }

        if (selectedCustomer.zip) {
          tempNewInitValues.zip = selectedCustomer.zip;
        }

        const ec = selectedCustomer.emergencyContacts?.[0];
        if (ec) {
          if (ec.firstName) tempNewInitValues.ecFirstName = ec.firstName;
          if (ec.lastName) tempNewInitValues.ecLastName = ec.lastName;
          if (ec.phoneNumber) tempNewInitValues.ecPhoneNumber = ec.phoneNumber;
          if (ec.email) tempNewInitValues.ecEmail = ec.email;
        }

        setLoadedInitValues(tempNewInitValues);
      } else {
        setLoadedInitValues(initFormValues);
      }
    }
    loadFormData();
  }, [selectedCustomer]);

  const openChat = React.useCallback(
    async (customerId: string) => {
      if (!loadedUser?.business || !customerId) return;
      await openCustomerChat({
        openLoadingModal,
        closeLoadingModal,
        navigate,
        user: loadedUser,
        customerId,
      });
    },
    [closeLoadingModal, loadedUser, navigate, openLoadingModal],
  );

  return (
    <Box
      style={{
        width: 900,
        margin: 'auto',
      }}>
      <Formik
        enableReinitialize
        initialValues={loadedInitValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          if (loadedUser) {
            let customerResponse;

            if (!selectedCustomer) {
              try {
                customerResponse = await addCustomerDatabase(
                  {
                    ...values,
                  },
                  loadedUser.business.id,
                );
              } catch (error) {
                if ((error as any).response.data.message) {
                  setFormError((error as any).response.data.message);
                } else if (error instanceof Error) {
                  setFormError(error.message);
                } else {
                  setFormError('An unexpected error occurred.'); // Some generic message
                }

                toast.error('Error creating customer');
                if (setShouldScrollToTop) {
                  setShouldScrollToTop(true);
                }

                setSubmitting(false); // End the submission process here
                return; // Exit the onSubmit function
              }
            } else {
              try {
                customerResponse = await updateCustomer(
                  {
                    ...values,
                  },
                  selectedCustomer.customerId,
                  loadedUser.business.id,
                  true,
                );
              } catch (error) {
                if ((error as any).response.data.message) {
                  setFormError((error as any).response.data.message);
                } else if (error instanceof Error) {
                  setFormError(error.message);
                } else {
                  setFormError('An unexpected error occurred.'); // Some generic message
                }

                toast.error('Error updating customer');
                if (setShouldScrollToTop) {
                  setShouldScrollToTop(true);
                }

                setSubmitting(false); // End the submission process here
                return; // Exit the onSubmit function
              }
            }

            const customerId =
              customerResponse.customerId || customerResponse.userId;
            try {
              await addCustomerAlgolia(customerId, loadedUser.business.id);
            } catch (error) {
              // Do something with the error
            }

            toast.success('Customer successfully updated!');
            setModalOpen(false);

            setSubmitting(false);

            await invalidateCustomerOverview(customerId);
            await onCustomerSave?.();

            if (openChatOnSave) {
              await openChat(customerId);
            }
          }
        }}>
        {({ isSubmitting, isValid }) => (
          <Form>
            {formError && (
              <Typography variant="body1" color="primary">
                {formError}
              </Typography>
            )}

            <div className="input-group">
              <Typography variant="h2" component="h2">
                Customer Information
              </Typography>
              <div className="inputs">
                <div className="input-row">
                  <TextInput
                    label="First Name *"
                    name="firstName"
                    disabled={disableCustomerEdit}
                    placeholder="John"
                  />
                  <TextInput
                    label="Last Name"
                    name="lastName"
                    disabled={disableCustomerEdit}
                    placeholder="Doe"
                  />
                </div>

                <div className="input-row">
                  <StyledPhoneInput
                    placeholder="Phone Number"
                    name="phoneNumber"
                    label="Phone *"
                    disabled={disableCustomerEdit}
                  />
                  <TextInput
                    label="Email"
                    name="email"
                    type="email"
                    placeholder="Email"
                    disabled={disableCustomerEdit}
                  />
                </div>

                <div className="input-row">
                  <TextInput
                    label="Birthday"
                    name="dob"
                    type="date"
                    disabled={disableCustomerEdit}
                  />
                </div>

                <div className="input-row">
                  <TextareaInput
                    name="internalNotes"
                    label="Internal Notes"
                    placeholder="Optional. Enter any notes for this customer. These notes are just for you and your team, and can not be viewed by the client."
                  />
                </div>
              </div>
              <div className="input-group-errors">
                <div className="error-message">
                  <ErrorMessage name="phoneNumber" />
                </div>
                <div className="error-message">
                  <ErrorMessage name="firstName" />
                </div>
                <div className="error-message">
                  <ErrorMessage name="lastName" />
                </div>
                <div className="error-message">
                  <ErrorMessage name="email" />
                </div>
                <div className="error-message">
                  <ErrorMessage name="dob" />
                </div>
                <div className="error-message">
                  <ErrorMessage name="internalNotes" />
                </div>
              </div>
            </div>

            <div className="input-group">
              <Typography variant="h2" component="h2">
                Customer Address
              </Typography>
              <div className="inputs">
                <div className="input-row">
                  <TextInput
                    label="Street Address"
                    name="streetAddress"
                    placeholder="123 Main St"
                    disabled={disableCustomerEdit}
                  />
                  <TextInput
                    label="Unit/Apt"
                    name="streetAddress2"
                    placeholder="Apt 4B"
                    disabled={disableCustomerEdit}
                  />
                </div>

                <div className="input-row">
                  <TextInput
                    label="City"
                    name="city"
                    placeholder="New York"
                    disabled={disableCustomerEdit}
                  />
                  <TextInput
                    label="State"
                    name="state"
                    placeholder="NY"
                    disabled={disableCustomerEdit}
                  />
                </div>

                <div className="input-row">
                  <TextInput
                    label="Zip"
                    name="zip"
                    placeholder="10001"
                    disabled={disableCustomerEdit}
                  />
                </div>
              </div>
            </div>

            <div className="input-group">
              <Typography variant="h2" component="h2">
                Emergency Contact
              </Typography>
              <div className="inputs">
                <div className="input-row">
                  <TextInput
                    label="First Name"
                    name="ecFirstName"
                    placeholder="Jane"
                    disabled={disableCustomerEdit}
                  />
                  <TextInput
                    label="Last Name"
                    name="ecLastName"
                    placeholder="Doe"
                    disabled={disableCustomerEdit}
                  />
                </div>

                <div className="input-row">
                  <StyledPhoneInput
                    placeholder="Phone Number"
                    name="ecPhoneNumber"
                    label="Phone"
                    disabled={disableCustomerEdit}
                  />
                  <TextInput
                    label="Email"
                    name="ecEmail"
                    type="email"
                    placeholder="Email"
                    disabled={disableCustomerEdit}
                  />
                </div>
              </div>
              <div className="input-group-errors">
                <div className="error-message">
                  <ErrorMessage name="ecPhoneNumber" />
                </div>
                <div className="error-message">
                  <ErrorMessage name="ecEmail" />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || !isValid}
              variant="contained"
              className="primary">
              {submitText ||
                (selectedCustomer ? 'Save Changes' : 'Add Customer')}
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
